import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PersonalLogo from '../PersonalLogo1.png';
import PersonalLogo2 from '../PersonalLogo2.png';
import GitHubIcon from '../GitHubIcon.png';
import LinkedInIcon from '../LinkedInIcon.png';
import './Navbar.css';

function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [sticky, setSticky] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setSticky(currentScrollY > 0 && currentScrollY < lastScrollY);
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <nav className={`bg-slate-900 transition-transform duration-300 ${sticky ? 'sticky top-0 z-50 ' : ''}`}>
      <div className="flex items-center justify-between w-full h-16 md:pr-8 p-2">
        
        {/* Logo on the left */}
        <Link to="/" className="mr-auto flex items-center ml-4 lg:ml-20 xl:ml-35 space-x-2">
          <img src={PersonalLogo} alt="Eli's Logo" className="h-10 md:h-12 lg:size-12" />

          {/* Social Media Buttons (visible on desktop) */}
          <div className="hidden md:flex space-x-2">
            <a href="https://github.com/EliNichols6" target="_blank" rel="noopener noreferrer">
              <img src={GitHubIcon} alt="GitHub" className="size-4" />
            </a>
            <a href="https://www.linkedin.com/in/elinichols0591/" target="_blank" rel="noopener noreferrer">
              <img src={LinkedInIcon} alt="LinkedIn" className="size-4" />
            </a>
          </div>
        </Link>

        {/* Mobile menu toggle button */}
        <button
          onClick={toggleMobileMenu}
          type="button"
          className={`hamb ${isMobileMenuOpen ? 'active' : ''} p-2 md:hidden`}
          aria-controls="navbar"
          aria-expanded={isMobileMenuOpen}
        >
          <span className="sr-only">Open Menu</span>
          <svg className="ham" viewBox="0 0 100 100" width="60" height="60">
            <path className="line top" d="m 30,33 h 40 c 3.722839,0 7.5,3.126468 7.5,8.578427 0,5.451959 -2.727029,8.421573 -7.5,8.421573 h -20"></path>
            <path className="line middle" d="m 30,50 h 40"></path>
            <path className="line bottom" d="m 70,67 h -40 c 0,0 -7.5,-0.802118 -7.5,-8.365747 0,-7.563629 7.5,-8.634253 7.5,-8.634253 h 20"></path>
          </svg>
        </button>

        {/* Full-screen overlay mobile menu */}
        <div
          className={`fixed inset-0 bg-gradient-to-t from-stone-900 via-blue-950 to-stone-900 z-50 transition-transform duration-500 transform ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          } md:hidden`}
        >
          {/* Logo in the center of mobile menu top bar */}
          <div className="pt-16 flex flex-col items-center py-4 space-y-4">
            <h3 className="text-white text-5xl sm:text-7xl font-extrabold font-roboto justify-center ml-6">Eli Nichols</h3>            
            {/* Social Media Buttons */}
            <div className="flex space-x-4 pt-2">
              <a href="https://github.com/EliNichols6" target="_blank" rel="noopener noreferrer">
                <img src={GitHubIcon} alt="GitHub" className="h-8 w-8" />
              </a>
              <a href="https://www.linkedin.com/in/elinichols0591/" target="_blank" rel="noopener noreferrer">
                <img src={LinkedInIcon} alt="LinkedIn" className="h-8 w-8" />
              </a>
            </div>
          </div>
          {/* Mobile menu links */}
          <ul className="flex flex-col items-center justify-start h-full pt-24 space-y-10 text-white text-4xl font-roboto font-normal">
            <li>
              <Link
                to="/"
                onClick={closeMobileMenu}
                className="flex items-center space-x-4 hover:text-slate-200/50"
              >
                {/* Home SVG */}
                <svg
                  stroke="currentColor"
                  fill="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 1024 1024"
                  height="1.2em"
                  width="1.2em"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginBottom: "2px" }}
                >
                  <path d="M946.5 505L560.1 118.8l-25.9-25.9a31.5 31.5 0 0 0-44.4 0L77.5 505a63.9 63.9 0 0 0-18.8 46c.4 35.2 29.7 63.3 64.9 63.3h42.5V940h691.8V614.3h43.4c17.1 0 33.2-6.7 45.3-18.8a63.6 63.6 0 0 0 18.7-45.3c0-17-6.7-33.1-18.8-45.2zM568 868H456V664h112v204zm217.9-325.7V868H632V640c0-22.1-17.9-40-40-40H432c-22.1 0-40 17.9-40 40v228H238.1V542.3h-96l370-369.7 23.1 23.1L882 542.3h-96.1z"></path>
                </svg>
                <span>Home</span>
              </Link>
            </li>

            <li>
              <Link
                to="/projects"
                onClick={closeMobileMenu}
                className="flex items-center space-x-4 hover:text-slate-200/50"
              >
                {/* Projects SVG */}
                <svg
                  stroke="currentColor"
                  fill="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 1024 1024"
                  height="1.2em"
                  width="1.2em"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginBottom: "2px" }}
                >
                  <path d="M312.1 591.5c3.1 3.1 8.2 3.1 11.3 0l101.8-101.8 86.1 86.2c3.1 3.1 8.2 3.1 11.3 0l226.3-226.5c3.1-3.1 3.1-8.2 0-11.3l-36.8-36.8c-3.1-3.1-8.2-3.1-11.3 0L517 485.3l-86.1-86.2c-3.1-3.1-8.2-3.1-11.3 0L275.3 543.4c-3.1 3.1-3.1 8.2 0 11.3l36.8 36.8z"></path>
                  <path d="M904 160H548V96c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H120c-17.7 0-32 14.3-32 32v520c0 17.7 14.3 32 32 32h356.4v32L311.6 884.1c-3.7 2.4-4.7 7.3-2.3 11l30.3 47.2v0.1c2.4 3.7 7.4 4.7 11.1 2.3L512 838.9l161.3 105.8c3.7 2.4 8.7 1.4 11.1-2.3v-0.1l30.3-47.2c2.4-3.7 1.3-8.6-2.3-11L548 776.3V744h356c17.7 0 32-14.3 32-32V192c0-17.7-14.3-32-32-32z m-40 512H160V232h704v440z"></path>
                </svg>
                <span>Projects</span>
              </Link>
            </li>

            <li>
              <Link
                to="/resume"
                onClick={closeMobileMenu}
                className="flex items-center space-x-4 hover:text-slate-200/50"
              >
                {/* Resume SVG */}
                <svg
                  stroke="currentColor"
                  fill="none"
                  strokeWidth="0"
                  viewBox="0 0 24 24"
                  height="1.2em"
                  width="1.2em"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginBottom: "2px" }}
                >
                  <path d="M7 18H17V16H7V18Z" fill="currentColor"></path>
                  <path d="M17 14H7V12H17V14Z" fill="currentColor"></path>
                  <path d="M7 10H11V8H7V10Z" fill="currentColor"></path>
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z"
                    fill="currentColor"
                  ></path>
                </svg>
                <span>Resume</span>
              </Link>
            </li>
          </ul>

        </div>

        {/* Desktop menu */}
        <div className="hidden md:flex md:justify-end md:mr-6 lg:mr-8 xl:mr-20 md:space-x-8 lg:space-x-10 xl:space-x-12 text-white font-semibold md:text-3xl lg:text-3xl xl:text-4xl font-roboto">
        {/* Home Link */}
        <Link to="/" className="nav-link flex items-center space-x-2">
          <svg
            stroke="currentColor"
            fill="currentColor"
            strokeWidth="0"
            viewBox="0 0 1024 1024"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginBottom: "2px" }}
          >
            <path d="M946.5 505L560.1 118.8l-25.9-25.9a31.5 31.5 0 0 0-44.4 0L77.5 505a63.9 63.9 0 0 0-18.8 46c.4 35.2 29.7 63.3 64.9 63.3h42.5V940h691.8V614.3h43.4c17.1 0 33.2-6.7 45.3-18.8a63.6 63.6 0 0 0 18.7-45.3c0-17-6.7-33.1-18.8-45.2zM568 868H456V664h112v204zm217.9-325.7V868H632V640c0-22.1-17.9-40-40-40H432c-22.1 0-40 17.9-40 40v228H238.1V542.3h-96l370-369.7 23.1 23.1L882 542.3h-96.1z"></path>
          </svg>
          <span>Home</span>
        </Link>

        {/* Projects Link */}
        <Link to="/projects" className="nav-link flex items-center space-x-2">
          <svg
            stroke="currentColor"
            fill="currentColor"
            strokeWidth="0"
            viewBox="0 0 1024 1024"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginBottom: "2px" }}
          >
            <path d="M312.1 591.5c3.1 3.1 8.2 3.1 11.3 0l101.8-101.8 86.1 86.2c3.1 3.1 8.2 3.1 11.3 0l226.3-226.5c3.1-3.1 3.1-8.2 0-11.3l-36.8-36.8c-3.1-3.1-8.2-3.1-11.3 0L517 485.3l-86.1-86.2c-3.1-3.1-8.2-3.1-11.3 0L275.3 543.4c-3.1 3.1-3.1 8.2 0 11.3l36.8 36.8z"></path>
            <path d="M904 160H548V96c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H120c-17.7 0-32 14.3-32 32v520c0 17.7 14.3 32 32 32h356.4v32L311.6 884.1c-3.7 2.4-4.7 7.3-2.3 11l30.3 47.2v0.1c2.4 3.7 7.4 4.7 11.1 2.3L512 838.9l161.3 105.8c3.7 2.4 8.7 1.4 11.1-2.3v-0.1l30.3-47.2c2.4-3.7 1.3-8.6-2.3-11L548 776.3V744h356c17.7 0 32-14.3 32-32V192c0-17.7-14.3-32-32-32z m-40 512H160V232h704v440z"></path>
          </svg>
          <span>Projects</span>
        </Link>

        {/* Resume Link */}
        <Link to="/resume" className="nav-link flex items-center space-x-2">
          <svg
            stroke="currentColor"
            fill="none"
            strokeWidth="0"
            viewBox="0 0 24 24"
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginBottom: "2px" }}
          >
            <path d="M7 18H17V16H7V18Z" fill="currentColor"></path>
            <path d="M17 14H7V12H17V14Z" fill="currentColor"></path>
            <path d="M7 10H11V8H7V10Z" fill="currentColor"></path>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z"
              fill="currentColor"
            ></path>
          </svg>
          <span>Resume</span>
        </Link>
      </div>
      </div>
    </nav>
  );
}

export default Navbar;
